import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { signIn, getCurrentUser } from 'aws-amplify/auth';
import { TextField, Box, Alert, Typography, Link } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { loginSchema } from '../../validations/user';
import GradientCircularProgress from '../common/GradientCircularProgress';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import BorderedButton from '../common/buttons/BorderedButton';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import PrimaryUnderlinedLink from '../common/PrimaryUnderlinedLink';
import { sendOtp, validateOtp } from '../../utils/user';
import useBackendApi from '../../hooks/useBackendApi';
import { fetchUserMembershipSummary, getOrgLandingPath } from '../../utils/userMembership';

const app_co = process.env.REACT_APP_COMPANY;

const UserLogin: React.FC = () => {
  const [error, setError] = useState<any | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useThemeContext();
  const md = useMediaQuery(theme.breakpoints.up('sm'));
  const { apiFetchRequest } = useBackendApi();
  const [showEnterCode, setShowEnterCode] = useState(false);

  const { pathname = '/', search = '' } = (location.state as any)?.from || {};
  const loginToPath = `${pathname}${search}`;

  const resolvePostLoginPath = useCallback(
    async (fallbackPath: string): Promise<string> => {
      if (app_co !== 'frost') {
        return fallbackPath;
      }

      const shouldCheckOrgLanding = fallbackPath === '/' || fallbackPath.startsWith('/?');
      if (!shouldCheckOrgLanding) {
        return fallbackPath;
      }

      const membershipSummary = await fetchUserMembershipSummary(apiFetchRequest);
      if (!membershipSummary) {
        return fallbackPath;
      }

      return (
        getOrgLandingPath(membershipSummary.organizationMemberships, membershipSummary.memberships) ?? fallbackPath
      );
    },
    [apiFetchRequest],
  );

  useEffect(() => {
    async function checkAuthState() {
      try {
        await getCurrentUser();
        const postLoginPath = await resolvePostLoginPath(loginToPath);
        navigate(postLoginPath, { replace: true });
      } catch (error) {
        setIsChecking(false);
      }
    }
    checkAuthState();
  }, [navigate, loginToPath, resolvePostLoginPath]);

  const handleSubmit = async (values: any) => {
    setError(null);
    try {
      const somethingUnexpectedErrorMessage =
        'Something unexpected happened, please try again, or contact support@relentlesshealth.com .';

      if (!showEnterCode) {
        const otpSentResponse = await sendOtp({ email: values.email }, apiFetchRequest);

        if (otpSentResponse && otpSentResponse.body) {
          if (!otpSentResponse.body.account_registered) {
            setShowEnterCode(false);
            if (app_co === 'rh') {
              setError(
                <>
                  Email not found. If you're taking your first test go to the{' '}
                  <Link component={RouterLink} to="/guest-activate.html">
                    activation page
                  </Link>
                  . Click{' '}
                  <Link component={RouterLink} to={'/support.html'}>
                    support
                  </Link>{' '}
                  for help.
                </>,
              );
            } else if (app_co === 'frost') {
              setError(
                <>
                  Email not found,{' '}
                  <Link component={RouterLink} to={'/signup'}>
                    create an account
                  </Link>
                  , or go to{' '}
                  <Link component={RouterLink} to={'/support.html'}>
                    support
                  </Link>{' '}
                  for help.
                </>,
              );
            } else {
              setError(somethingUnexpectedErrorMessage);
            }
            return;
          }

          if (otpSentResponse.body.sent_otp) {
            setShowEnterCode(true);
          } else if (otpSentResponse.body.message === 'USER_NOT_FOUND') {
            setError('Email not found, please create a new account.');
          } else {
            setError(somethingUnexpectedErrorMessage);
          }
        } else {
          setError(somethingUnexpectedErrorMessage);
        }
      } else if (showEnterCode) {
        const validateOtpResponse = await validateOtp(
          { email: values.email, confirmation_code: values.otp },
          apiFetchRequest,
        );

        if (validateOtpResponse && validateOtpResponse.body && validateOtpResponse.body.validated_otp) {
          const { isSignedIn, nextStep } = await signIn({
            username: values.email,
            password: validateOtpResponse.body.tmp_pwd,
          });

          if (isSignedIn) {
            setIsChecking(true);
            const postLoginPath = await resolvePostLoginPath(loginToPath);
            navigate(postLoginPath, { replace: true });
          } else {
            console.log('Additional step required:', nextStep);
            navigate('/signup');
          }
        } else {
          setError('Code was not valid. Please try again.');
        }
      } else {
        setError(somethingUnexpectedErrorMessage);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'An error occurred during sign in');
    }
  };

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      email: '',
      otp: '',
    },
    validationSchema: loginSchema,
    onSubmit: handleSubmit,
  });

  if (isChecking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        textAlign: 'center',
        padding: '10px 44px 27px 44px',
        marginTop: '32px',
        borderRadius: '16px',
        backgroundColor: theme.custom.pages.userLogin.backgroundColor,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: 800,
          margin: 'auto',
          paddingRight: '3%',
          paddingLeft: '0',
          mt: 4,
          textAlign: 'left',
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: theme.custom.pages.userLogin.typography.h4.color }} gutterBottom>
            {app_co === 'frost' ? (
              <Typography variant="h3" sx={{ width: { sm: '200px', md: '400px' }, typography: { sm: 'h5', md: 'h3' } }}>
                Sign in to FROST
              </Typography>
            ) : (
              <>Welcome back to Relentless Health!</>
            )}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: theme.custom.pages.userLogin.typography.subtitle1.color }}
            gutterBottom
          >
            {!showEnterCode && 'Please enter your email'}
            {showEnterCode && "We've sent a one-time-passcode (OTP) to your email"}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box>
            <TextField
              fullWidth
              label="Email Address"
              autoComplete="email"
              autoFocus
              required
              {...formik.getFieldProps('email')}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              margin="normal"
              InputLabelProps={{
                sx: { color: theme.custom.pages.userLogin.textField.label.color },
              }}
            />
            {showEnterCode && (
              <TextField
                fullWidth
                label="Enter code"
                autoComplete="otp"
                autoFocus
                {...formik.getFieldProps('otp')}
                error={formik.touched.otp && Boolean(formik.errors.otp)}
                helperText={formik.touched.otp && formik.errors.otp}
                margin="normal"
                InputLabelProps={{
                  sx: { color: theme.custom.pages.userLogin.textField.label.color },
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'flex-start' },
              maxWidth: { xs: '100%', sm: 'auto' },
            }}
          >
            <PrimaryBlueButton
              isLoading={formik.isSubmitting}
              onClick={() => formik.handleSubmit()}
              buttonText="Next"
              additionalProps={{
                disabled: !formik.isValid || formik.isSubmitting,
              }}
            />
            <BorderedButton
              sxProps={{
                height: '41px',
                marginLeft: { xs: '0', sm: '10px' },
                marginTop: { xs: '10px', sm: '0' },
              }}
              onClick={() => navigate('/')}
              buttonText={'Cancel'}
            />
          </Box>
        </Box>
        {app_co === 'frost' ? (
          <></>
        ) : (
          <>
            <Box sx={{ mt: 2, width: '100%' }}>
              <PrimaryUnderlinedLink navigateTo="/activate.html" text="Activate test kit and create your account" />
            </Box>
          </>
        )}
      </Box>
      {md && (
        <Box sx={{ maxWidth: '492px', maxHeight: '360px', marginTop: '30px' }}>
          <img src="/images/login.png" alt="Login" style={{ maxWidth: '492px', maxHeight: '360px' }} />
        </Box>
      )}
    </Box>
  );
};

export default UserLogin;
